import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, ArrowRight } from "lucide-react";
import { SELECTED_WORK } from "@/lib/pulseConstants";
import { VideoModal } from "@/components/shared/VideoModal";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";

/**
 * Mobile Selected Work carousel.
 *
 * A touch-first, CSS scroll-snap horizontal carousel that replaces the
 * three large stacked case-study rows on mobile. Each card shows ONE
 * concise summary sentence, at most two visible tags, a primary CTA and
 * (only where useful) a clean secondary CTA. Full descriptive copy remains
 * on the individual project pages.
 *
 * Behavioural rules:
 *   - one card visible at a time with ~12–15% of the next card as a peek
 *   - CSS scroll snap (mandatory) + smooth scroll
 *   - manual swipe only (no autoplay)
 *   - progress indicator: `01 / 03` counter + dots
 *   - no page-level horizontal overflow (container width fills 100vw and
 *     the internal scroller uses padding for the peek)
 *
 * Accessibility:
 *   - `role="region"` with `aria-roledescription="carousel"`
 *   - each slide is a `role="group"` with `aria-label` telling users which
 *     item of how many they are viewing
 *   - dot pagination uses real `<button>`s with `aria-label` and
 *     `aria-current`
 *   - scroll snap does not block vertical page scrolling (touch-action:
 *     pan-y remains via default overflow-x semantics)
 */
export default function MobileSelectedWorkCarousel() {
    const items = SELECTED_WORK.filter((c) => c.revealed);
    const scrollerRef = useRef(null);
    const slideRefs = useRef([]);
    const [current, setCurrent] = useState(0);
    const [videoItem, setVideoItem] = useState(null);

    // Observe which slide is centred in the scroller.
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const idx = Number(entry.target.getAttribute("data-slide-index"));
                        if (!Number.isNaN(idx)) setCurrent(idx);
                    }
                });
            },
            { root: scroller, threshold: [0.6, 0.9] }
        );
        slideRefs.current.forEach((el) => { if (el) io.observe(el); });
        return () => io.disconnect();
    }, [items.length]);

    function scrollTo(index) {
        const el = slideRefs.current[index];
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        }
    }

    if (items.length === 0) return null;

    return (
        <section
            id="selected-work"
            className="section-pad relative scroll-mt-24"
            data-testid="selected-work-mobile-section"
        >
            <div className="container-editorial">
                {/* Section header (kept concise for mobile) */}
                <div className="mb-8">
                    <SectionEyebrow index="02" label="Selected Work" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Proof before <span className="text-[#FF2A2A]">promise.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="body-md mt-4">
                            Real brand experiences designed to earn attention, participation and a life beyond the event.
                        </p>
                    </Reveal>
                </div>
            </div>

            {/* Full-bleed scroller — sits outside the max-width container so the
                peek can extend to the viewport edge without causing overflow. */}
            <div
                ref={scrollerRef}
                role="region"
                aria-roledescription="carousel"
                aria-label="Selected Work"
                data-testid="selected-work-mobile-scroller"
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 px-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                style={{ scrollPaddingLeft: "1.25rem" }}
            >
                {items.map((item, i) => (
                    <MobileWorkCard
                        key={item.key}
                        item={item}
                        index={i}
                        total={items.length}
                        registerRef={(el) => { slideRefs.current[i] = el; }}
                        onOpenVideo={() => setVideoItem(item)}
                    />
                ))}
            </div>

            {/* Pagination — counter + dots */}
            <div className="container-editorial mt-5">
                <div className="flex items-center justify-between gap-4" data-testid="selected-work-mobile-pagination">
                    <div
                        className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60"
                        data-testid="selected-work-mobile-counter"
                        aria-live="polite"
                    >
                        <span className="text-white">{String(current + 1).padStart(2, "0")}</span>
                        <span className="mx-2 text-white/30">/</span>
                        <span>{String(items.length).padStart(2, "0")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {items.map((item, i) => (
                            <button
                                key={item.key}
                                type="button"
                                aria-label={`Go to ${item.title}`}
                                aria-current={i === current ? "true" : undefined}
                                onClick={() => scrollTo(i)}
                                data-testid={`selected-work-mobile-dot-${i}`}
                                className={`h-2.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${i === current ? "w-6 bg-[#FF2A2A]" : "w-2.5 bg-white/25 hover:bg-white/50"}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <VideoModal
                open={Boolean(videoItem)}
                onOpenChange={(v) => { if (!v) setVideoItem(null); }}
                title={videoItem ? videoItem.title : ""}
                description={videoItem ? videoItem.summary : ""}
                poster={videoItem && videoItem.video ? videoItem.video.poster : ""}
                posterAlt={videoItem && videoItem.video ? videoItem.video.posterAlt : ""}
                sources={videoItem && videoItem.video ? videoItem.video.sources : []}
                videoId={videoItem ? videoItem.slug : ""}
                placement="mobile_selected_work"
            />
        </section>
    );
}

function MobileWorkCard({ item, index, total, registerRef, onOpenVideo }) {
    const hasVideo = Boolean(item.video && item.video.sources && item.video.sources.length);
    // Compressed tag list (max 2) so cards stay short on mobile.
    const visibleTags = (item.categories || []).slice(0, 2);
    // Choose a concise summary — the first sentence of `summary`.
    const conciseSummary = (item.summary || "").split(/(?<=[.!?])\s+/)[0];

    return (
        <article
            ref={registerRef}
            data-slide-index={index}
            data-testid={`selected-work-mobile-card-${item.slug}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${item.title} — ${index + 1} of ${total}`}
            className="snap-start shrink-0 w-[86%] max-w-[380px] rounded-2xl border border-white/10 bg-[#0B0C10] overflow-hidden"
        >
            <ImageAffordance item={item} hasVideo={hasVideo} onOpenVideo={onOpenVideo} />

            <div className="p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#FF2A2A]">
                        {item.client}
                    </span>
                    <span className="font-display text-3xl leading-none text-white/85" aria-hidden="true">
                        {item.number}
                    </span>
                </div>

                <h3 className="font-display text-2xl leading-tight text-white">
                    {item.title}
                </h3>

                {visibleTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {visibleTags.map((cat) => (
                            <span
                                key={cat}
                                className="px-2 py-1 rounded-full border border-white/15 text-[10px] font-mono text-white/75 uppercase tracking-[0.18em]"
                            >
                                {cat}
                            </span>
                        ))}
                    </div>
                )}

                <p className="body-md mt-4 text-white/80">
                    {conciseSummary}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-4">
                    {hasVideo ? (
                        <>
                            <button
                                type="button"
                                onClick={onOpenVideo}
                                data-testid={`case-study-mobile-cta-${item.slug}`}
                                className="group inline-flex items-center gap-2 min-h-11 rounded-full bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold text-sm px-5"
                            >
                                <Play size={14} className="ml-0.5" /> Watch Film
                            </button>
                            <Link
                                to={`/work/${item.slug}`}
                                data-testid={`case-study-mobile-detail-${item.slug}`}
                                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors min-h-11 py-3"
                            >
                                View Project <ArrowUpRight size={12} />
                            </Link>
                        </>
                    ) : (
                        <Link
                            to={`/work/${item.slug}`}
                            data-testid={`case-study-mobile-cta-${item.slug}`}
                            className="group inline-flex items-center gap-2 min-h-11 rounded-full bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold text-sm px-5"
                        >
                            View Project <ArrowUpRight size={14} />
                        </Link>
                    )}
                </div>
            </div>
        </article>
    );
}

function ImageAffordance({ item, hasVideo, onOpenVideo }) {
    const inner = (
        <div className="relative w-full bg-[#0B0C10] overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
            <img
                src={item.primary_image}
                alt={item.primary_image_alt}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: item.primary_image_position || "center" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/10 pointer-events-none" />

            {hasVideo && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-14 w-14 rounded-full border border-white/40 bg-black/50 backdrop-blur flex items-center justify-center text-white shadow-[0_10px_40px_-10px_rgba(255,42,42,0.6)]" aria-hidden="true">
                        <Play size={22} className="ml-0.5" />
                    </div>
                </div>
            )}

            <div className="absolute bottom-3 right-3">
                <div className="inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur border border-white/20 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white">
                    <span>{hasVideo ? "Watch Film" : "Tap to preview"}</span>
                    <ArrowRight size={12} />
                </div>
            </div>
        </div>
    );

    if (hasVideo) {
        return (
            <button
                type="button"
                onClick={onOpenVideo}
                aria-label={`Watch the ${item.title} film`}
                className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                data-testid={`case-study-mobile-video-trigger-${item.slug}`}
            >
                {inner}
            </button>
        );
    }

    return (
        <Link
            to={`/work/${item.slug}`}
            aria-label={`View ${item.title} project details`}
            className="block"
            data-testid={`case-study-mobile-image-link-${item.slug}`}
        >
            {inner}
        </Link>
    );
}
